/**
 * Order Log — shared model (ORD-1.2 / ORD-1.3 / ORD-1.4)
 * ─────────────────────────────────────────────────────────────────────────────
 * One normalised row shape + one column registry for every Order Log surface
 * (Snapshot Paid drill, Snapshot Unpaid drill, Sales Order Log, mobile cards).
 *
 * `orderLogRow.ts` maps one API order onto this shape. A component the endpoint
 * does not return stays null and renders as an em dash — never as a zero.
 */
import type { RefundType } from '../../refunds/refundModel';

export type OrderLogStatus = 'paid' | 'cancelled';

/** Financial waterfall. `null` = the value is unavailable, NOT zero (ORD-1.4). */
export interface OrderLogFinancials {
    itemTotal: number | null;
    discount: number | null;
    serviceCharge: number | null;
    deliveryFee: number | null;
    otherCharge: number | null;
    tax: number | null;
    tips: number | null;
    gratuity: number | null;
    processingFee: number | null;
    grandTotal: number | null;
    /** Total amount refunded on this order — `null` when the source doesn't carry it. */
    refundAmount: number | null;
}

export interface OrderLogRow {
    /** React key + row identity */
    key: string;
    /** Original API item — handed back to the caller on row click */
    raw: any;
    orderNo: string;
    orderId: string;
    orderDate: string | null;
    orderTime: string | null;
    orderType: string;
    customerName: string | null;
    customerPhone: string | null;
    /** Display identifier, already collapsed to "R4 +1" for merged checks */
    tableName: string | null;
    /** Full merged-table list — tooltip / slide-over / export */
    tableList: string[];
    guests: number | null;
    staffName: string | null;
    itemPreview: string | null;
    cancelReason: string | null;
    isThirdParty: boolean;
    hasRefund: boolean;
    /** REF-5 — Full / Item / Fee, or `null` for an order with no refund event. */
    refundType: RefundType | null;
    /**
     * REF-5 — total cash returned. Deliberately OUTSIDE `financials`: a refund is a
     * later event against the order, not a component of what the guest originally
     * paid, so `rowFoots()`'s waterfall sum never has to know to exclude it.
     */
    refunded: number | null;
    financials: OrderLogFinancials;
}

// ─── Column registry ─────────────────────────────────────────────────────────

export type OrderLogColumnGroup = 'operations' | 'financial';

export interface OrderLogColumnDef {
    key: string;
    label: string;
    group: OrderLogColumnGroup;
    /** grid-template-columns track */
    width: string;
    align?: 'left' | 'right' | 'center';
    /** sort id, null = not sortable */
    sortKey?: string | null;
    /** first click direction */
    defaultDir?: 'asc' | 'desc';
    /** always rendered, cannot be switched off */
    locked?: boolean;
    /** always rendered AND kept out of the "/ n" total (Grand Total) */
    pinned?: boolean;
    /** summed in the totals footer */
    numeric?: boolean;
    /** rendered with a leading minus sign */
    reduction?: boolean;
}

const OPS: OrderLogColumnDef[] = [
    { key: 'orderNo', label: 'Order ID', group: 'operations', width: '0.95fr', sortKey: null, locked: true },
    { key: 'orderDate', label: 'Date', group: 'operations', width: '1.05fr', sortKey: null, locked: true },
    { key: 'orderTime', label: 'Time', group: 'operations', width: '0.95fr', sortKey: 'orderTime', defaultDir: 'desc' },
    { key: 'orderType', label: 'Order Type', group: 'operations', width: '1fr', sortKey: 'orderType', defaultDir: 'asc' },
    { key: 'customerName', label: 'Customer', group: 'operations', width: '1.05fr', sortKey: 'customerName', defaultDir: 'asc' },
    { key: 'customerPhone', label: 'Phone', group: 'operations', width: '1.1fr', sortKey: null },
    { key: 'tableName', label: 'Table No.', group: 'operations', width: '1.05fr', sortKey: 'tableName', defaultDir: 'asc' },
    { key: 'guests', label: 'Guests', group: 'operations', width: '0.7fr', sortKey: 'guests', defaultDir: 'desc' },
    { key: 'staffName', label: 'Staff', group: 'operations', width: '1.1fr', sortKey: 'staffName', defaultDir: 'asc' },
    { key: 'itemPreview', label: 'Items', group: 'operations', width: '1.9fr', sortKey: null },
    // REF-5 — Full / Item / Fee, or an em dash. A fact about the order, so it sits
    // with Order Details rather than the financial waterfall.
    { key: 'refundType', label: 'Refund Type', group: 'operations', width: '1fr', sortKey: null },
];

/** Fixed waterfall order — never re-ordered by the user (ORD-1.4). */
const FINANCIAL: OrderLogColumnDef[] = [
    { key: 'itemTotal', label: 'Item Total', group: 'financial', width: '1fr', align: 'right', numeric: true },
    { key: 'discount', label: 'Discount', group: 'financial', width: '1fr', align: 'right', numeric: true, reduction: true },
    { key: 'deliveryFee', label: 'Delivery Fee', group: 'financial', width: '1.05fr', align: 'right', numeric: true },
    // Labelled for the API field it holds (convenienceFee); the key stays `otherCharge`
    // so column selections and presets saved before the rename keep working.
    { key: 'otherCharge', label: 'Convenience Fee', group: 'financial', width: '1.25fr', align: 'right', numeric: true },
    { key: 'tax', label: 'Tax', group: 'financial', width: '0.85fr', align: 'right', numeric: true },
    { key: 'tips', label: 'Tips', group: 'financial', width: '0.85fr', align: 'right', numeric: true },
    { key: 'gratuity', label: 'Gratuity', group: 'financial', width: '0.95fr', align: 'right', numeric: true },
    // Only for outlets that capture a service charge (`hasServiceCharge`, from the outlet's
    // `uiFeatureFlags.isServiceChargeEnabled`) — otherwise it is absent from both the table and
    // the Columns menu, never a column of $0. Optional, defaults off (see the presets below);
    // sits between Gratuity and Card Processing Fee, matching the Financial Breakdown table
    // and the Order Detail panel.
    { key: 'serviceCharge', label: 'Service Charge', group: 'financial', width: '1.1fr', align: 'right', numeric: true },
    // Label matches the order summary's "Card Processing Fee"; the key is unchanged so saved column selections still apply.
    { key: 'processingFee', label: 'Card Processing Fee', group: 'financial', width: '1.5fr', align: 'right', numeric: true },
];

/** Pinned last column. Replaces the old "AMOUNT" head — the two never co-exist. */
const GRAND_TOTAL: OrderLogColumnDef = {
    key: 'grandTotal', label: 'Grand Total', group: 'financial', width: '1.05fr',
    align: 'right', sortKey: 'amount', defaultDir: 'desc', numeric: true, pinned: true, locked: true,
};

/**
 * REF-5 — total cash returned, in red, em dash when none. Sits AFTER Grand Total on
 * purpose: it is not part of the additive chain `rowFoots()` checks (see `OrderLogRow.refunded`),
 * because a refund is a later event against the order, not a component of what the guest paid.
 */
const REFUNDED: OrderLogColumnDef = {
    key: 'refunded', label: 'Refunded', group: 'financial', width: '1fr',
    align: 'right', sortKey: null, numeric: true,
};

/** Replaces the Items column while the status is Cancelled (ORD-1.3). */
const REASON: OrderLogColumnDef = { key: 'cancelReason', label: 'Reason', group: 'operations', width: '1.9fr', sortKey: null };

export interface OrderLogColumnSetOptions {
    /** Outlet has table service — false hides Table No. entirely (never a column of dashes) */
    hasTableService?: boolean;
    /** Cancelled: swap Items → Reason */
    status?: OrderLogStatus;
    /** Unpaid log has no financial waterfall — Grand Total only */
    financialGroup?: boolean;
    /** House-retained-gratuity outlets suppress the Gratuity column (TG v1.1) */
    hasGratuity?: boolean;
    /**
     * Outlet captures a service charge (`uiFeatureFlags.isServiceChargeEnabled`). Defaults to
     * false, so the Service Charge column is offered only when a caller opts in.
     */
    hasServiceCharge?: boolean;
    /**
     * Per-surface sort ids — the Unpaid log sorts Staff as `serverName` and
     * cannot sort Customer at all. `null` makes a column unsortable.
     */
    sortKeys?: Record<string, string | null>;
}

/** Full registry for a surface, in menu order. Grand Total is always last. */
export function buildOrderLogColumns(opts: OrderLogColumnSetOptions = {}): OrderLogColumnDef[] {
    const { hasTableService = true, status = 'paid', financialGroup = true, hasGratuity = true, hasServiceCharge = false, sortKeys } = opts;
    const cancelled = status === 'cancelled';
    const ops = OPS
        .filter(c => (c.key === 'tableName' ? hasTableService : true))
        // REF-5 — a cancelled order was never collected, so it cannot carry a refund;
        // the column would be a wall of em dashes there.
        .filter(c => (c.key === 'refundType' ? !cancelled : true))
        .map(c => (c.key === 'itemPreview' && cancelled ? REASON : c));
    const fin = financialGroup
        ? FINANCIAL.filter(c => (c.key === 'gratuity' ? hasGratuity : true)).filter(c => (c.key === 'serviceCharge' ? hasServiceCharge : true))
        : [];
    // REF-5 — only excluded for `cancelled`; the Unpaid Orders log calls this with the
    // default 'paid' status (it isn't a third `status` value) and now carries real
    // `refundType`/`refunded` fields off /sales/live/v2/unpaid-orders, same as Paid Orders.
    const all = [...ops, ...fin, GRAND_TOTAL, ...(financialGroup && !cancelled ? [REFUNDED] : [])];
    if (!sortKeys) return all;
    return all.map(c => (Object.prototype.hasOwnProperty.call(sortKeys, c.key) ? { ...c, sortKey: sortKeys[c.key] } : c));
}

/** Columns switched on by default — matches the "Operations" preset. */
export const OPERATIONS_PRESET = ['orderNo', 'orderDate', 'orderTime', 'orderType', 'customerName', 'tableName', 'guests', 'staffName', 'itemPreview', 'cancelReason', 'refundType', 'refunded'];

/** "Financial" preset — the whole waterfall plus the order identity columns. */
export const FINANCIAL_PRESET = [
    'orderNo', 'orderDate', 'orderTime', 'orderType',
    'itemTotal', 'discount', 'deliveryFee', 'otherCharge', 'tax', 'tips', 'gratuity', 'serviceCharge', 'processingFee', 'refunded',
];

export function defaultVisibleColumns(columns: OrderLogColumnDef[]): string[] {
    return columns.filter(c => c.pinned || c.locked || OPERATIONS_PRESET.indexOf(c.key) >= 0).map(c => c.key);
}

/**
 * "10/20" — columns on screen out of columns in the registry.
 *
 * Both sides count EVERY column, locked and pinned included. Counting the
 * pinned Grand Total in the numerator but not the denominator produced
 * "20/19" once every box was ticked; and the numerator is taken straight from
 * `visibleColumnDefs` so the figure can never drift from what the table
 * actually renders.
 */
export function visibleColumnDefs(columns: OrderLogColumnDef[], visible: string[]): OrderLogColumnDef[] {
    return columns.filter(c => c.pinned || c.locked || visible.indexOf(c.key) >= 0);
}

export function columnCountLabel(columns: OrderLogColumnDef[], visible: string[]): string {
    return `${visibleColumnDefs(columns, visible).length}/${columns.length}`;
}


// ─── Sorting ─────────────────────────────────────────────────────────────────

/** Sort ids the API understands — everything else is sorted client-side. */
export const SERVER_SORT_KEYS = ['orderTime', 'orderType', 'customerName', 'guests', 'amount'];

/**
 * Natural alphanumeric comparison so T1 < T2 < T10 (ORD-1.2).
 */
export function naturalCompare(a: string, b: string): number {
    const ax: (string | number)[] = [];
    const bx: (string | number)[] = [];
    String(a).toLowerCase().replace(/(\d+)|(\D+)/g, (_m, n, s) => { ax.push(n ? Number(n) : s); return ''; });
    String(b).toLowerCase().replace(/(\d+)|(\D+)/g, (_m, n, s) => { bx.push(n ? Number(n) : s); return ''; });
    for (let i = 0; i < Math.max(ax.length, bx.length); i++) {
        const av = ax[i]; const bv = bx[i];
        if (av === undefined) return -1;
        if (bv === undefined) return 1;
        if (typeof av === 'number' && typeof bv === 'number') { if (av !== bv) return av - bv; continue; }
        const as = String(av); const bs = String(bv);
        if (as !== bs) return as < bs ? -1 : 1;
    }
    return 0;
}

const FIN_KEYS = ['itemTotal', 'discount', 'serviceCharge', 'deliveryFee', 'otherCharge', 'tax', 'tips', 'gratuity', 'processingFee', 'grandTotal'];

/**
 * Client-side sort for the columns the API cannot sort on (table, staff, the
 * financial waterfall). Rows with no value group LAST in both directions, and
 * the incoming Time-descending order is preserved as the stable secondary key.
 */
export function sortRowsClientSide(rows: OrderLogRow[], sortKey: string | null, dir: 'asc' | 'desc'): OrderLogRow[] {
    if (!sortKey) return rows;
    const value = (r: OrderLogRow): string | number | null => {
        if (sortKey === 'amount') return r.financials.grandTotal;
        if (FIN_KEYS.indexOf(sortKey) >= 0) return (r.financials as any)[sortKey];
        const v = (r as any)[sortKey];
        return v === undefined ? null : v;
    };
    const indexed = rows.map((row, i) => ({ row, i }));
    indexed.sort((a, b) => {
        const av = value(a.row); const bv = value(b.row);
        const aEmpty = av === null || av === undefined || av === '';
        const bEmpty = bv === null || bv === undefined || bv === '';
        if (aEmpty && bEmpty) return a.i - b.i;      // both dashes → keep Time order
        if (aEmpty) return 1;                         // dashes always last
        if (bEmpty) return -1;
        const cmp = typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : naturalCompare(String(av), String(bv));
        if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
        return a.i - b.i;                             // stable secondary Time-desc
    });
    return indexed.map(x => x.row);
}

// ─── Cell value access ───────────────────────────────────────────────────────

export function rowValue(row: OrderLogRow, key: string): any {
    if (FIN_KEYS.indexOf(key) >= 0) return (row.financials as any)[key];
    return (row as any)[key];
}

/** Components must foot to the Grand Total, else the row is flagged (ORD-1.4). */
export function rowFoots(row: OrderLogRow): boolean {
    const f = row.financials;
    if (f.grandTotal === null) return true;
    const parts = [f.itemTotal, f.serviceCharge, f.deliveryFee, f.otherCharge, f.tax, f.tips, f.gratuity, f.processingFee];
    if (parts.some(p => p === null)) return true;     // unavailable ≠ discrepancy
    const sum = parts.reduce((s: number, p) => s + (p as number), 0) - (f.discount || 0);
    return Math.abs(sum - f.grandTotal) < 0.01;
}
